import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Heart, CalendarDays, Loader2 } from "lucide-react";
import { cutOpeningSound } from "@/lib/openingSound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACKS = [
  { id: "aide", label: "Ça m'a aidée" },
  { id: "peu", label: "Peu de changement" },
  { id: "pas_adapte", label: "Pas adapté aujourd'hui" },
];

const KIND_LABELS = {
  respiration: "Respiration",
  douceur: "Douceur",
  visualisation: "Visualisation",
  mouvement: "Mouvement",
  meditation: "Méditation",
  texte: "Lecture",
};

// Espace « Mon accompagnement » : la patiente retrouve UNIQUEMENT les contenus
// que Julie lui a personnellement attribués — accessible SANS abonnement RAV+.
// Pas de texte libre : validations simples uniquement (confidentialité).
export default function MonAccompagnement() {
  const navigate = useNavigate();
  const [accomp, setAccomp] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [validating, setValidating] = useState(null);
  const [openCard, setOpenCard] = useState(0); // une carte dépliée à la fois — page courte
  const token = localStorage.getItem("rav-token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    cutOpeningSound(); // jamais de vague ici
    if (!token) {
      navigate("/connexion");
      return;
    }
    axios
      .get(`${API}/mon-accompagnement`, { headers })
      .then(({ data }) => {
        setAccomp(data.accompaniment);
        setLoaded(true);
      })
      .catch(() => navigate("/accueil"));
  }, []);

  const validate = async (contentId, feedback = null) => {
    setValidating(contentId);
    try {
      await axios.post(`${API}/mon-accompagnement/valider`, { content_id: contentId, feedback }, { headers });
      setAccomp((a) => ({
        ...a,
        my_validations: {
          ...a.my_validations,
          [contentId]: { content_id: contentId, done_at: new Date().toISOString(), feedback: feedback || a.my_validations?.[contentId]?.feedback || null },
        },
      }));
    } catch {
      /* silencieux */
    }
    setValidating(null);
  };

  if (!loaded) return null;

  return (
    <div data-testid="mon-accompagnement-page" style={{ maxWidth: 560, margin: "0 auto", padding: "12px 16px 110px", overflowY: "auto" }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        {/* En-tête compact : petit logo, titre sobre — la place va aux pratiques */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <img src="/logo-accueil.webp" alt="Respire Aime Vibre" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover" }} />
          <p className="rav-page-sub" style={{ margin: "4px 0 0" }}>Votre suivi personnalisé</p>
          <h1 className="rav-page-title" data-testid="accomp-title" style={{ margin: "2px 0 0", fontSize: "1.5rem" }}>Mon accompagnement</h1>
        </div>

        {!accomp ? (
          <p className="rav-page-sub" data-testid="accomp-none" style={{ textAlign: "center", marginTop: 24 }}>
            Votre espace accompagnement est prêt. Dès que Julie vous attribuera des pratiques, elles apparaîtront ici.
          </p>
        ) : (
          <>
            {accomp.note && (
              <div className="rav-card" data-testid="accomp-note-card" style={{ padding: "18px 20px", marginBottom: 20, textAlign: "left" }}>
                <p style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, marginBottom: 8 }}>
                  <Heart size={16} strokeWidth={1.8} /> Un mot de Julie
                </p>
                <p style={{ fontStyle: "italic", lineHeight: 1.6 }}>« {accomp.note} »</p>
              </div>
            )}
            {accomp.next_appointment && (
              <p className="rav-page-sub" data-testid="accomp-next-rdv" style={{ textAlign: "center", marginBottom: 20 }}>
                <CalendarDays size={14} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                Prochain rendez-vous : {accomp.next_appointment}
              </p>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {accomp.contents.map((c, i) => {
                const v = accomp.my_validations?.[c.id];
                const mediaUrl = c.media_path ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${c.media_path}` : null;
                const soundUrl = c.sound_path ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${c.sound_path}` : null;
                const isVideo = mediaUrl && /\.(mp4|webm|mov)$/i.test(c.media_path);
                const isImage = mediaUrl && /\.(png|jpe?g|webp|gif)$/i.test(c.media_path);
                const isOpen = openCard === i;
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="rav-card"
                    data-testid={`accomp-content-${i}`}
                    style={{ padding: "12px 16px", textAlign: "left" }}
                  >
                    {/* Ligne compacte : toujours visible, un toucher déplie */}
                    <button
                      type="button"
                      data-testid={`accomp-toggle-${i}`}
                      onClick={() => setOpenCard(isOpen ? -1 : i)}
                      style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, width: "100%" }}
                    >
                      <span style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, display: "block" }}>{c.title}</span>
                        <span className="rav-page-sub">{KIND_LABELS[c.kind] || c.kind} · {c.minutes} min</span>
                      </span>
                      {v && <Check size={16} strokeWidth={2.4} color="#7a9b6d" data-testid={`accomp-check-${i}`} />}
                      <span className="rav-page-sub" style={{ fontSize: "1.1rem" }}>{isOpen ? "−" : "+"}</span>
                    </button>

                    {isOpen && (
                      <div style={{ marginTop: 10 }}>
                        {c.description && <p style={{ fontSize: "0.88rem", lineHeight: 1.55, marginBottom: 10 }}>{c.description}</p>}
                        {isVideo && <video src={mediaUrl} controls playsInline data-testid={`accomp-video-${i}`} style={{ width: "100%", borderRadius: 12, marginBottom: 10, maxHeight: 260 }} />}
                        {isImage && <img src={mediaUrl} alt={c.title} data-testid={`accomp-image-${i}`} style={{ width: "100%", borderRadius: 12, marginBottom: 10, maxHeight: 260, objectFit: "cover" }} />}
                        {soundUrl && <audio src={soundUrl} controls data-testid={`accomp-audio-${i}`} style={{ width: "100%", height: 40, marginBottom: 10 }} />}
                      </div>
                    )}

                    {v ? (
                      <div data-testid={`accomp-done-${i}`} style={{ marginTop: isOpen ? 0 : 6 }}>
                        <p style={{ display: "flex", alignItems: "center", gap: 6, color: "#7a9b6d", fontWeight: 600, fontSize: "0.88rem", margin: 0 }}>
                          Fait le {new Date(v.done_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                        </p>
                        {v.feedback ? (
                          <p className="rav-page-sub" data-testid={`accomp-feedback-done-${i}`} style={{ marginTop: 2 }}>
                            Votre retour : {FEEDBACKS.find((f) => f.id === v.feedback)?.label}
                          </p>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                            {FEEDBACKS.map((f) => (
                              <button
                                key={f.id}
                                type="button"
                                className="rav-secondary-button"
                                data-testid={`accomp-feedback-${f.id}-${i}`}
                                onClick={() => validate(c.id, f.id)}
                                style={{ fontSize: "0.78rem", padding: "4px 10px" }}
                              >
                                {f.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="rav-primary-button"
                        data-testid={`accomp-validate-${i}`}
                        disabled={validating === c.id}
                        onClick={() => validate(c.id)}
                        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: isOpen ? 0 : 8, padding: "8px 16px", fontSize: "0.88rem" }}
                      >
                        {validating === c.id ? <Loader2 size={14} className="rav-spin" /> : <Check size={14} strokeWidth={2.2} />}
                        Je l'ai fait
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
